import React from 'react';
import PropTypes from 'prop-types';
import ErrorMessage from './ErrorMessage';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import styled, { createGlobalStyle } from 'styled-components';
import { normalizePortalText } from 'utils/baochauTheme';
import useFormControl from './useFormControl';
import { getFormPopupContainer } from './popupHelpers';
import './style.scss';

dayjs.extend(customParseFormat);

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

const DatePickerGlobalStyle = createGlobalStyle`
  body .bw_form_date_picker_dropdown.ant-picker-dropdown {
    z-index: var(--portal-z-dropdown, 1050) !important;
  }

  body .bw_form_date_picker_dropdown .ant-picker-panel-container {
    border-radius: 10px;
    box-shadow: 0 18px 44px rgba(15, 23, 42, 0.18);
  }
`;

const DatePickerStyle = styled(DatePicker)`
  width: 100%;

  &.ant-picker {
    min-height: 44px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid #d6dee9;
    background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .ant-picker-input input {
    color: #101828 !important;
    font-size: 14px;
  }

  .ant-picker-input {
    flex: 1;
  }

  .ant-picker-input input::placeholder {
    color: #98a2b3;
  }

  .ant-picker-suffix,
  .ant-picker-clear {
    color: #667085;
  }

  &.is-invalid {
    border-color: #fda29b;
    box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.1), 0 8px 18px rgba(240, 68, 56, 0.06);
  }

  &.ant-picker-focused,
  &:hover {
    border-color: #f2aaa5;
    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1), 0 8px 18px rgba(15, 23, 42, 0.06);
  }
`;

const parseDateValue = (value, format) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;

  const formats = Array.isArray(format) ? format : [format || DEFAULT_DATE_FORMAT];
  for (const item of formats.filter(Boolean)) {
    const parsed = dayjs(value, item, true);
    if (parsed.isValid()) return parsed;
  }

  const fallback = dayjs(value);
  return fallback.isValid() ? fallback : null;
};

const FormDatePicker = ({
  field,
  validation,
  placeholder,
  disabled = false,
  style,
  format,
  bordered,
  allowClear,
  popupClassName: customPopupClassName,
  classNames,
  getPopupContainer,
  readOnly = false,
  id,
  className,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const pickerFormat = format || DEFAULT_DATE_FORMAT;
  const { describedBy, error, errorId, fieldValue, hasError, inputId, setFieldValue } = useFormControl({
    field,
    validation,
    id,
    ariaDescribedBy,
  });
  const popupClassName = ['bw_form_date_picker_dropdown', customPopupClassName].filter(Boolean).join(' ');
  // Ant Design's picker compares its value by reference. Re-parsing a stored
  // string into a new Dayjs object on every render can make the picker update
  // its internal state indefinitely ("Maximum update depth exceeded").
  const pickerValue = React.useMemo(
    () => parseDateValue(fieldValue, pickerFormat),
    [fieldValue, pickerFormat],
  );

  return (
    <React.Fragment>
      <DatePickerGlobalStyle />
      <DatePickerStyle
        id={inputId}
        data-field={field}
        className={['bw_form_control', className, hasError ? 'is-invalid' : '', readOnly ? 'is-readonly' : '']
          .filter(Boolean)
          .join(' ')}
        allowClear={allowClear}
        disabled={disabled || readOnly}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        getPopupContainer={getPopupContainer || getFormPopupContainer}
        placeholder={typeof placeholder === 'string' ? normalizePortalText(placeholder) : placeholder}
        classNames={{
          ...classNames,
          popup: {
            ...classNames?.popup,
            root: [classNames?.popup?.root, popupClassName].filter(Boolean).join(' '),
          },
        }}
        value={pickerValue}
        onChange={(e, date) => {
          if (date) {
            setFieldValue(date);
          } else {
            setFieldValue('');
          }
        }}
        format={pickerFormat}
        style={style}
        {...props}
      />
      {error && <ErrorMessage id={errorId} message={error?.message} />}
    </React.Fragment>
  );
};

FormDatePicker.propTypes = {
  field: PropTypes.string,
  validation: PropTypes.object,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  format: PropTypes.string,
  allowClear: PropTypes.bool,
  bordered: PropTypes.bool,
  popupClassName: PropTypes.string,
  getPopupContainer: PropTypes.func,
};

export default FormDatePicker;
